import { useEffect, useState } from "react";
import { DynamicDropdownOption } from "./manageDynamicDropdowns";
import styled, { useTheme, css } from "styled-components";
import {
  getDynamicDropdownByCategory,
  getDropdownCategories,
  updateDynamicDropdownOption,
  deleteDynamicDropdownOption,
  createDropdownOption,
} from "./manageDynamicDropdowns";
import { useAlert } from "../../../components/Alert/Alerts";
import Button from "../../../components/Button/Button";
import { CardBody } from "../../../components/DashboardCard/DashboardCard";
import TextField from "../../../components/FormFields/TextField";
import { FormikProvider, useFormik } from "formik";
import DragDropdownOptionList from "../../../components/Draggable/DraggableDynamicDropdownOptions";
import { FocusBorder } from "../../../components/FormFields/FieldTemplate";
import { SelectBox } from "../Judging/JudgingConfig/EntrySetTab";

export const OptionsSection = styled.div`
  display: flex;
  width: 50%;
  align-self: stretch;
  flex: 1;
  gap: 3rem;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colorBorderLight};
  max-width: 50%;
  min-height: 400px;
`;

export const StyledCategoryCard = styled.div<{ isSelected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colorBackgroundLight};
  padding: 0.5rem;
  padding-left: calc(0.5rem + 2px);
  border: solid 1px ${({ theme }) => theme.colorBorderLight};
  cursor: pointer;

  ${FocusBorder} {
    left: 0;
  }

  ${(p) =>
    !p.isSelected &&
    css`
      &:hover {
        & > ${FocusBorder} {
          width: 4px;
          background: ${({ theme }) => theme.colorActivation};
        }
      }
    `};

  ${(p) =>
    p.isSelected &&
    css`
      background: ${({ theme }) => theme.colorSelected};
    `};

  .category-display {
    /* display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden; */
    display: flex;
    align-items: center;
    min-height: 56px;
    margin-right: auto;
  }
`;

const AdminDropdownConfig = () => {
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [options, setOptions] = useState<DynamicDropdownOption[]>([]);
  const [newOption, setNewOption] = useState("");
  const theme = useTheme();
  const { addNewAlert } = useAlert();

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {},
  });

  useEffect(() => {
    getDropdownCategories()
      .then((res) => {
        if (res.status === 200) setCategories(res.data);
      })
      .catch(() => {
        addNewAlert({ type: "error", message: "Failed to load categories." });
      });
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      getDynamicDropdownByCategory(selectedCategory)
        .then((res) => {
          if (res.status === 200) {
            setOptions(res.data);
          } else {
            setOptions([]); // avoid null
          }
        })
        .catch(() => {
          // If category is new and has no options, just use an empty list
          setOptions([]);
        });
    }
  }, [selectedCategory]);

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    if (categories.includes(newCategory)) {
      addNewAlert({ type: "error", message: "Category already exists." });
      return;
    }
    setCategories((prev) => [...prev, newCategory]);
    setSelectedCategory(newCategory);
    setNewCategory("");
    setOptions([]); // clear options for new category
  };

  const handleAddOption = () => {
    const trimmed = newOption.trim();
    if (!trimmed || !selectedCategory) return;

    if (
      options.some((opt) => opt.name.toLowerCase() === trimmed.toLowerCase())
    ) {
      addNewAlert({ type: "error", message: "Option already exists." });
      return;
    }

    const newOptionObj: DynamicDropdownOption = {
      id: 0,
      category: selectedCategory,
      name: trimmed,
      order: options.length,
    };

    createDropdownOption(newOptionObj)
      .then((res) => {
        if (res.status === 201) {
          setOptions((prev) => [...prev, res.data]);
          setNewOption("");
          addNewAlert({ type: "success", message: "Option added." });
        }
      })
      .catch(() => {
        addNewAlert({ type: "error", message: "Failed to add option." });
      });
  };

  return (
    <FormikProvider value={formik}>
      <CardBody className="flex gap-[2rem]">
        {/* Category List */}
        <div className="w-[400px]">
          <h2 className="mb-[1rem] mt-[1.5rem]">Categories</h2>
          <ul className="flex flex-col gap-[.5rem] mb-[1rem]">
            {categories.length > 0 ? (
              categories.map((category) => (
                <StyledCategoryCard
                  key={category}
                  isSelected={selectedCategory === category}
                  onClick={() => setSelectedCategory(category)}
                >
                  <FocusBorder />
                  <p className="category-display">{category}</p>
                </StyledCategoryCard>
              ))
            ) : (
              <p className="italic text-gray-500">No categories yet</p>
            )}
          </ul>

          <div className="flex gap-[.5rem]">
            <TextField
              name="newCategory"
              placeholder="New Category"
              className="w-[300px]"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            />
            <Button icon="plus" onClick={handleAddCategory}>
              Add
            </Button>
          </div>
        </div>

        {/* Options List */}
        <OptionsSection>
          <div className="flex-1">
            <h2 className="mb-[1rem]">Options</h2>
            {selectedCategory ? (
              <>
                <div className="flex gap-[.5rem] mb-[1rem]">
                  <TextField
                    name="newOption"
                    className="w-[300px]"
                    placeholder="New Option"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                  />
                  <Button icon="plus" onClick={handleAddOption}>
                    Add
                  </Button>
                </div>

                <DragDropdownOptionList
                  dropdownOptions={options}
                  setSelectedOption={() => {}}
                  onEdit={({ id, name }) => {
                    updateDynamicDropdownOption({
                      id,
                      propertyName: "name",
                      propertyValue: name,
                    }).then((res) => {
                      if (res.status === 200) {
                        setOptions((prev) =>
                          prev.map((opt) =>
                            opt.id === id ? { ...opt, name } : opt
                          )
                        );
                        addNewAlert({
                          type: "success",
                          message: "Option updated",
                        });
                      }
                    });
                  }}
                  onDelete={(id) =>
                    deleteDynamicDropdownOption(id).then(() => {
                      setOptions((prev) => prev.filter((o) => o.id !== id));
                      addNewAlert({
                        type: "success",
                        message: "Option deleted.",
                      });
                    })
                  }
                />
              </>
            ) : (
              <SelectBox>Please select a category</SelectBox>
            )}
          </div>
        </OptionsSection>
      </CardBody>
    </FormikProvider>
  );
};

export default AdminDropdownConfig;
